/**
 * Theme preference: three states, not two.
 *
 * 'system' (the default) leaves the OS in charge via prefers-color-scheme and stamps nothing,
 * so a machine that switches at sunset takes the portal with it. 'light' and 'dark' stamp
 * data-theme on the root element, which theme.css gives higher precedence than the media query
 * in BOTH directions — that is what lets someone pick light on a dark-mode laptop.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

export const themeStorageKey = 'kt-theme';

export function readStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(themeStorageKey);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    // Private browsing / blocked storage: fall back to following the OS rather than failing.
    return 'system';
  }
}

export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', preference);
  }

  try {
    if (preference === 'system') localStorage.removeItem(themeStorageKey);
    else localStorage.setItem(themeStorageKey, preference);
  } catch {
    // Preference just won't persist; the current page is still themed correctly.
  }
}

/** What the user is actually looking at right now, resolving 'system' against the OS. */
export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
